/**
 * The words each docs page says around its content (`code` and [links](/developers/ask)): its lede and the few
 * lines beside its fields and code. The pages and their Markdown (_lib/markdown) both read them, so an agent
 * reads what a person reads.
 */
import type { Auth } from "../_lib/reference/api";

/** The developer site, as its layout names and describes it. */
export const SITE = {
  title: "Vestra developers",
  says: "Put Bash inside your product: one card of eighteen commands, from TypeScript, Python, the CLI, MCP or plain HTTP.",
} as const;

/** The quickstart's lede, and how long it takes. */
export const QUICKSTART_LEDE =
  "Set your Bash up from a few plain words, then ask it about a cancelled flight for one of your users, answer what it asks, answer a call to your own tool, and see what it cost.";
export const QUICKSTART_TAKES = "About fifteen minutes, all in test mode.";

export const GUIDES_LEDE =
  "Each guide is one task, told as short steps, with its code in your language beside each step. Every sample is checked against the API's own contract.";

export const REFERENCE_LEDE =
  "The exact shape of everything, read from the code that runs it: plain words first, then the fields.";
export const TOOLS_LINE =
  "For tools: the contract as [an OpenAPI explorer](/developers/reference/openapi), its [`v1.json`](/developers/v1.json), and vestra.yaml's [`JSON Schema`](/developers/vestra.schema.json) for your editor.";

export const API_LEDE =
  "Every route, by what it is for. Each group's page says what each route does, every field it takes and answers, and shows it in your language. Send `Authorization: Bearer vk_test_...` on every call.";
/** Under a group's name, and a route's. */
export const SEND_KEY =
  "Send `Authorization: Bearer vk_test_...` on every call.";

export const FILE_LEDE =
  "One file sets up your Bash: what it does, the tools it may call, what Bash asks before doing, its tests and its routines. `vestra deploy` applies it, and so does `PUT /v1/deployments/{name}` with the file as JSON. Only `name` is required.";

/** Beside the file's fields: the whole file, and how an editor checks it (the file's schema at `schemaId`). */
export const WHOLE_FILE_SAYS = (schemaId: string): string =>
  `Beside these, a whole file: the travel help desk the [first guide](/developers/guides/travel-help-desk) builds, what \`vestra init\` drafted and the one line the guide adds. Its first line, \`# yaml-language-server: $schema=${schemaId}\`, has your editor check the file as you type.`;

export const EVENTS_LEDE =
  "One catalog names every event. A conversation's stream carries what happens as it happens; your webhooks get what settles. Each event says where it arrives, what it means, and every field of its `data`.";
export const EVENTS_ARRIVE = {
  title: "How each arrives",
  says: "The SDKs hand you both as one event: `event.type`, `event.user` and `event.data`.",
  stream: "On the stream",
  webhook: "In a webhook",
};
export const NO_EVENT_DATA = "No data: it says only that it happened.";

export const ERRORS_LEDE =
  "Every error answers one body. Branch on `code`, never on the message; show the person `message` when it helps; and send us `request_id` when it is our side.";

/** The SDKs and CLI reference: how each door starts. */
export const SDK_SAYS = {
  typescript:
    "`import { Vestra } from '@vestra/sdk'`, then `new Vestra()`: each line of the card is a method, and `vestra.user(id)` acts for one of your users.",
  python:
    "`from vestra import Vestra`, then `Vestra()`: it blocks, and each call returns its answer. `AsyncVestra` has the same names, each awaited.",
  cli: "Every line takes `--user <id>` to act for one of your users, and `--json` to print the API's answer. `vestra <line> --help` says the rest.",
  http: "Over HTTP there is nothing to install: each line of the card is a route, and [the API reference](/developers/reference/api) has every one, with every field it takes and answers.",
  mcp: "Over MCP there is nothing to install: one endpoint, `/v1/mcp`, that any MCP client can call with your key, and `tools/list` names what it offers. [The mcp line](/developers/mcp) says how to connect.",
};

export const CHANGELOG_LEDE = "What changed, and when.";

/** Who may call a route. */
const WHO: Record<Auth, string> = {
  key: "Your API key",
  key_or_client: "Your API key, or a session's token",
  client: "A session's token",
  public: "No key: anyone may call it",
};

export function whoCalls(route: { auth: Auth; scope: string | null }): string {
  if (route.auth === "public" || route.auth === "client")
    return WHO[route.auth];
  if (route.scope === "any") return `${WHO[route.auth]}, any scope`;
  return `${WHO[route.auth]}, scope \`${route.scope}\` or \`all\``;
}

/** How a route's code was made: every call a checked sample, or its HTTP call made from the contract. */
export const ROUTE_CODE = {
  checked: "Every call here is a checked sample.",
  made: (params: boolean) =>
    `Over HTTP it is made from the contract: fill in ${params ? "each {…} and every value." : "every value."}`,
  /** Over a door with no sample of its own. */
  overHttp:
    "No sample in your language for this route, so here it is over HTTP.",
};

/** What a route takes and answers, beside its fields. */
export const ROUTE_TAKES = {
  nothing: "Nothing to send but the route.",
  body: (media: string, required: boolean) =>
    media === "multipart/form-data"
      ? "Body, as a form"
      : required
        ? "Body"
        : "Body, if you send one",
  /** An answer that is not JSON: a stream of events, or the file itself. */
  answer: (method: string, media: string | undefined): string | null =>
    media === "text/event-stream"
      ? method === "GET"
        ? "A stream of events; with format=json, a page of them."
        : "JSON, or a stream of events with stream: true."
      : media && media !== "application/json"
        ? `The file itself (${media}).`
        : null,
  protocol: "Its answer is a protocol of its own: its line's page says more.",
};
